import { useState } from "react";
import { AiOutlinePlus } from "react-icons/ai";
import DeleteModal from "../../../../../components/modals/DeleteModal";
import ActionPopover from "../../../../../components/popovers/actionPopover";
import { TaxCodeDto } from "../../../../../models/tax-code.dto";
import {
	useDeleteCompanyTaxCode,
	useGetCompanyTaxCode,
	useGetCompanyTaxCodeSearch,
} from "../../../../../services/company-tax-code.service";
import useEditFormStore from "../../../../../state-management/useEditFormStore";
import CompanyTaxCodeModalForm from "./modal";
import {
	useManageTableWithSearchParams,
	useManageTableWithSearchParamsData,
} from "../../../../../helpers/useManageTable";
import TableFilter from "../../../../../components/table/filter/TableFilter";
import APISearchInput from "../../../../../components/search/APISearchInput";
import { TaxCodesColumns } from "./tanstack-columns";
import TanStackEmployeeTable from "../../../../../components/table/TanStackEmployeeTable";
import { TableId } from "../../../../../models/table-configuration.dto";
import useCompanyStore from "../../../../../state-management/useCompanyStore";

const CompanyTaxCodes = () => {
	const { currentCompany } = useCompanyStore();
	const companyId = currentCompany?.id as number;

	const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
	const [openModal, setOpenModal] = useState(false);
	const [openDeleteModal, setOpenDeleteModal] = useState(false);
	const [isEditForm, setIsEditForm] = useState(false);

	const { mutate: searchTaxCodes } = useGetCompanyTaxCodeSearch();
	const {
		tableAPIRef,
		fetchedTableData,
		tableData,
		isLoading,
		pageCount,
		setTableData,
	} = useManageTableWithSearchParams<TaxCodeDto>({
		fetchHook: (params) => useGetCompanyTaxCode(companyId, params),
	});

	const handleClose = () => setAnchorEl(null);
	const handleOpenModal = () => {
		setAnchorEl(null);
		setIsEditForm(false);
		setOpenModal(true);
	};

	const { formDataValues, setFormData } = useEditFormStore();
	const [deleting, setDeleting] = useState(false);
	const { mutate: deleteTaxCode } = useDeleteCompanyTaxCode();
	const { reloadTableData } = useManageTableWithSearchParamsData();

	const handleDeleteMethod = () => {
		setDeleting(true);
		deleteTaxCode(
			{ companyId, id: formDataValues?.id },
			{
				onSuccess: () => {
					reloadTableData();
					setOpenDeleteModal(false);
				},
				onSettled: () => setDeleting(false),
			},
		);
	};

	return (
		<div>
			<h2 className="text-2sm font-semibold mb-3">Tax Codes</h2>
			<hr />
			<div className="flex justify-between items-center mt-7 mb-8">
				<div className="flex items-center gap-3">
					<APISearchInput
						fallbackData={fetchedTableData}
						setTableData={setTableData}
						mutate={searchTaxCodes}
					/>
					<TableFilter
						fields={[
							{
								title: "",
								fieldName: "",
								fieldType: "SORT",
								options: [
									{ title: "Code", fieldName: "code" },
									{ title: "Start Date", fieldName: "startDate" },
									{ title: "End Date", fieldName: "endDate" },
									{ title: "Effective Date", fieldName: "effectiveDate" },
									{ title: "Expiry Date", fieldName: "expiryDate" },
									{ title: "Date Created", fieldName: "createdAt" },
									{ title: "Date Modified", fieldName: "modifiedAt" },
								],
							},
							{
								title: "Enabled",
								fieldName: "enabled",
								fieldType: "YES_NO",
							},
							{
								title: "Default",
								fieldName: "isDefault",
								fieldType: "YES_NO",
							},
							{
								title: "Start Date",
								fieldName: "startDate",
								fieldType: "DATE_RANGE",
							},
							{
								title: "End Date",
								fieldName: "endDate",
								fieldType: "DATE_RANGE",
							},
							{
								title: "Effective Date",
								fieldName: "effectiveDate",
								fieldType: "DATE_RANGE",
							},
							{
								title: "Expiry Date",
								fieldName: "expiryDate",
								fieldType: "DATE_RANGE",
							},
						]}
					/>
				</div>
				<div
					className="flex items-center text-primary-blue text-sm cursor-pointer"
					onClick={handleOpenModal}
				>
					<AiOutlinePlus className="mr-1 text-sm" />
					Add New Tax Code
				</div>
			</div>
			<TanStackEmployeeTable
				tableId={TableId.TAX_CODES}
				data={tableData}
				columns={TaxCodesColumns}
				loading={isLoading}
				onRowClick={(row, event) => {
					setFormData(row);
					setAnchorEl(event.currentTarget);
				}}
				onActionClick={(row, event) => {
					setFormData(row);
					setAnchorEl(event.currentTarget);
				}}
				initialColumnVisibility={{}}
				pageSize={10}
				pageCount={pageCount}
				apiRef={tableAPIRef}
			/>
			<ActionPopover
				id={anchorEl ? "simple-popover" : undefined}
				open={Boolean(anchorEl)}
				anchorEl={anchorEl}
				handleClose={handleClose}
				setOpenDeleteModal={setOpenDeleteModal}
				setOpenEdit={setIsEditForm}
				setOpenModal={setOpenModal}
			/>
			<DeleteModal
				openDeleteModal={openDeleteModal}
				spanContent={formDataValues?.code || ""}
				setOpenDeleteModal={setOpenDeleteModal}
				handleDeleteMethod={handleDeleteMethod}
				deleting={deleting}
			/>
			{openModal && (
				<CompanyTaxCodeModalForm
					open={openModal}
					setOpen={setOpenModal}
					title={isEditForm ? "Update Tax Code" : "Add New Tax Code"}
					isEditForm={isEditForm}
					tableAPIRef={tableAPIRef}
					companyId={companyId}
					reloadTableData={reloadTableData}
				/>
			)}
		</div>
	);
};

export default CompanyTaxCodes;
